'use client';

import { useState, useEffect } from 'react';
import { 
  Target, CheckCircle, Star, BarChart3, TrendingUp, 
  AlertTriangle, Activity, Clock, TrendingDown 
} from 'lucide-react';
import {
  teamIndicators,
  organizationalIndicators,
  performanceCategories,
  getAllEmployeePerformance
} from '../data';
import type { PerformanceCategoryId as PerformanceCategoryType } from '../types';

export function PerformanceIndicatorsTab() {
  // State for performance data and filtering
  const [realEmployeePerformance, setRealEmployeePerformance] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPerformanceCategory, setSelectedPerformanceCategory] = useState<string | null>(null);

  // Load performance data asynchronously
  useEffect(() => {
    const loadPerformanceData = async () => {
      try {
        setIsLoading(true);
        const performanceData = await getAllEmployeePerformance();
        setRealEmployeePerformance(performanceData);
      } catch (error) {
        console.error('Error loading performance data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPerformanceData();
  }, []);
  
  const getPerformanceColor = (score: number) => {
    if (score >= 85) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getCategoryConfig = (category: PerformanceCategoryType) => {
    return performanceCategories.find(cat => cat.id === category) || performanceCategories[0];
  };

  const handlePerformanceCategoryClick = (categoryName: string) => {
    // Toggle filter - se já está selecionada, remove o filtro
    setSelectedPerformanceCategory(selectedPerformanceCategory === categoryName ? null : categoryName);
  };

  // Filter employees based on selected category
  const filteredEmployeePerformance = selectedPerformanceCategory 
    ? realEmployeePerformance.filter(emp => {
        // Map employee performance to category name
        const categoryConfig = getCategoryConfig(emp.category);
        return categoryConfig.name === selectedPerformanceCategory;
      })
    : realEmployeePerformance;

  return (
    <div className="space-y-8">
      {/* Indicadores de Equipe */}
      <div>
        <h3 className="font-display text-lg font-semibold text-white mb-4">Indicadores de Equipe</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <Target className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-brand-primary">{teamIndicators.competencyAdherence}%</div>
            <div className="metric-label">AVALIAÇÃO POR COMPETÊNCIA</div>
            <p className="text-xs text-brand-gray mt-2">Aderência às competências</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-success">{teamIndicators.individualGoalsAchieved}%</div>
            <div className="metric-label">META INDIVIDUAL (MIA)</div>
            <p className="text-xs text-brand-gray mt-2">Metas atingidas</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <Star className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-warning">{teamIndicators.evaluation360Average}</div>
            <div className="metric-label">AVALIAÇÃO 360°</div>
            <p className="text-xs text-brand-gray mt-2">Nota média</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-info">{teamIndicators.projectPerformance}%</div>
            <div className="metric-label">PERFORMANCE POR PROJETO</div>
            <p className="text-xs text-brand-gray mt-2">Entregas no prazo</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-brand-primary">{teamIndicators.continuousImprovement}%</div>
            <div className="metric-label">MELHORIA CONTÍNUA (IMC)</div>
            <p className="text-xs text-brand-gray mt-2">Iniciativas de melhoria</p>
          </div>
        </div>
      </div>

      {/* Indicadores Gerenciais e Organizacionais */}
      <div>
        <h3 className="font-display text-lg font-semibold text-white mb-4">Indicadores Gerenciais e Organizacionais</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-error">{organizationalIndicators.criticalPerformance}%</div>
            <div className="metric-label">DESEMPENHO CRÍTICO</div>
            <p className="text-xs text-brand-gray mt-2">Abaixo do padrão</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <Activity className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-warning">{organizationalIndicators.turnoverByPerformance.highPerformers}%</div>
            <div className="metric-label">TURNOVER ALTA PERFORMANCE</div>
            <p className="text-xs text-brand-gray mt-2">Saída de talentos</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <Clock className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-warning">{organizationalIndicators.turnoverByPerformance.averagePerformers}%</div>
            <div className="metric-label">TURNOVER PERFORMANCE MÉDIA</div>
            <p className="text-xs text-brand-gray mt-2">Performance padrão</p>
          </div>

          <div className="metric-card group h-full">
            <div className="flex items-center justify-between mb-4">
              <TrendingDown className="text-brand-primary" size={24} />
            </div>
            <div className="metric-value text-success">{organizationalIndicators.turnoverByPerformance.lowPerformers}%</div>
            <div className="metric-label">TURNOVER BAIXA PERFORMANCE</div>
            <p className="text-xs text-brand-gray mt-2">Necessita desenvolvimento</p>
          </div>
        </div>
      </div>

      {/* Legenda de Performance */}
      <div>
        <h3 className="font-display text-lg font-semibold text-white mb-4">Legenda de Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {performanceCategories.map((category) => {
            const Icon = category.icon;
            const isSelected = selectedPerformanceCategory === category.name;
            return (
              <div 
                key={category.id} 
                className={`card border-2 cursor-pointer transition-all duration-300 ${
                  isSelected 
                    ? `${category.borderColor} bg-brand-dark shadow-lg scale-105` 
                    : `${category.borderColor} hover:bg-brand-dark hover:scale-102`
                }`}
                onClick={() => handlePerformanceCategoryClick(category.name)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded ${category.color} ${isSelected ? 'shadow-lg' : ''}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className={`font-medium ${isSelected ? 'text-brand-primary' : 'text-white'}`}>
                      {category.name}
                    </h4>
                    <p className="text-xs text-brand-gray">{category.description}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-2xl font-bold ${isSelected ? 'text-brand-primary' : 'text-white'}`}>
                    {category.percentage}%
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs border ${
                    isSelected 
                      ? 'bg-brand-primary text-brand-dark border-brand-primary' 
                      : 'bg-brand-dark border-gray-600 text-gray-300'
                  }`}>
                    {category.count || 0} pessoas
                  </span>
                </div>
                {isSelected && (
                  <div className="mt-2 text-center">
                    <span className="text-xs text-brand-primary font-medium">
                      ✓ Filtro ativo - Clique novamente para remover
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Matriz de Performance dos Colaboradores */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-white">
            Matriz de Performance dos Colaboradores
          </h3>
          <div className="flex items-center gap-4">
            {selectedPerformanceCategory && (
              <button
                onClick={() => setSelectedPerformanceCategory(null)}
                className="text-sm text-brand-primary hover:text-white transition-colors flex items-center gap-1"
              >
                ✕ Limpar filtro
              </button>
            )}
            <span className="text-sm text-brand-gray">
              {selectedPerformanceCategory 
                ? `Mostrando ${filteredEmployeePerformance.length} funcionários de "${selectedPerformanceCategory}"`
                : `${filteredEmployeePerformance.length} funcionários`
              }
            </span>
          </div>
        </div>
        
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th>Colaborador</th>
                  <th>Competência</th>
                  <th>Meta (MIA)</th>
                  <th>360°</th>
                  <th>Projeto</th>
                  <th>Melhoria</th>
                  <th>Geral</th>
                  <th>Categoria</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredEmployeePerformance.length > 0 ? (
                  filteredEmployeePerformance.map((employee) => {
                    const categoryConfig = getCategoryConfig(employee.category);
                    const CategoryIcon = categoryConfig.icon;
                    const isHighlighted = selectedPerformanceCategory === categoryConfig.name;
                    
                    return (
                      <tr key={employee.id} className={isHighlighted ? 'bg-brand-dark' : ''}>
                        <td>
                          <div>
                            <div className="font-medium text-white">{employee.name}</div>
                            <div className="text-sm text-brand-gray">{employee.role}</div>
                            <div className="text-xs text-gray-500">{employee.department}</div>
                          </div>
                        </td>
                        <td>
                          <span className={getPerformanceColor(employee.competency)}>
                            {employee.competency}%
                          </span>
                        </td>
                        <td>
                          <span className={getPerformanceColor(employee.goals)}>
                            {employee.goals}%
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400" />
                            <span className="text-white">{employee.evaluation360}</span>
                          </div>
                        </td>
                        <td>
                          <span className={getPerformanceColor(employee.projectPerformance)}>
                            {employee.projectPerformance}%
                          </span>
                        </td>
                        <td>
                          <span className={employee.improvement >= 0 ? 'text-success' : 'text-error'}>
                            {employee.improvement >= 0 ? '+' : ''}{employee.improvement}%
                          </span>
                        </td>
                        <td>
                          <span className={getPerformanceColor(employee.overallScore)}>
                            {employee.overallScore}%
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${categoryConfig.color} ${isHighlighted ? 'shadow-lg' : ''}`}>
                              <CategoryIcon className="w-4 h-4" />
                            </div>
                            <span className={`text-xs ${isHighlighted ? 'text-brand-primary font-medium' : 'text-brand-gray'}`}>
                              {categoryConfig.name}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <div className="text-brand-gray">
                        Nenhum funcionário encontrado para a categoria selecionada
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}